/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
import React, { useState, useEffect, useContext, useRef } from 'react';
import Icon from 'components/Icon/index';
import { Field, Form } from 'formik';
import {ComboBox, InputField} from '@fitinsur/components';
import PropTypes from 'prop-types';

import { EmailContainer,
  EmailHeader,
  ModalTitle,
  ContPages,
  TitleContainer,
  EmailContent,
  FieldTitle,
  EmailFieldRow
} from './styles';
import { GlobalLoaderContext } from 'store/context';
import MessageCenter from 'utils/src/message-center';
import { TextEditorBox } from 'components/index';
import { ButtonContainer, ButtonContent } from 'components/Button/index';
import { Button } from 'components/index';
import { needsToUpdate } from 'flows/broking/pages/paymentPackSummaryDistribution/utils';

function EmailModal({ allParties,  currentParty, setCurrentParty, handleUpdateEmailContent, children, formikProps, setFieldValue }) {
  const [ numParties, setNumParties] = useState(0);
  const [index, setIndex] = useState(0);
  const [options, setOptions] = useState([]);
  const [firstChange, setFirstChange] = useState(true);

  useEffect(() => {
    const curParty = {
      idPersonInsurer: allParties[index]?.idPersonInsurer,
      insurerFirstName: allParties[index]?.insurerFirstName,
      contactsCC: renderEmails(allParties[index]?.contactsCC),
      contactsTo: renderEmails(allParties[index]?.contactsTo),
      subject: allParties[index]?.subject,
      emailBody: allParties[index]?.emailBody
    }
    setCurrentParty(curParty);
    setFieldValue('idPersonInsurer', allParties[index]?.idPersonInsurer);
    handleUpdateEmailContent(curParty, false)
  }, [index])

  
  const loading = useContext(GlobalLoaderContext);
  const mc = new MessageCenter();

  const handleAllParties = () => {

    const partyOptions = allParties.map((item) => ({ value: item.idPersonInsurer, label: item.insurerFirstName }));

    setOptions(partyOptions);
    setNumParties(allParties.length);
  }

  useEffect(() => {
    if (allParties.length) {
      handleAllParties();
    }
  }, [allParties])

  const handlePrevious = () => {
    try {
      if (index === 0) {
        return;
      }
      needsToUpdate(allParties[index], currentParty);
      const party = allParties[index - 1];
      setIndex(index - 1);
      setCurrentParty({
        idPersonInsurer: party?.idPersonInsurer,
        insurerFirstName: party?.insurerFirstName,
        contactsCC: renderEmails(party?.contactsCC),
        contactsTo: renderEmails(party?.contactsTo),
        subject: party?.subject,
        emailBody: party?.emailBody
      })
      setFirstChange(true);
    } catch (error) {
      mc.error(error);
    }
  };

  const handleNext = () => {
    try {
      if ((index + 1) < numParties) {
        needsToUpdate(allParties[index], currentParty);
        const party = allParties[index + 1];
        setIndex(index + 1);
        setCurrentParty({
          idPersonInsurer: party?.idPersonInsurer,
          insurerFirstName: party?.insurerFirstName,
          contactsCC: renderEmails(party?.contactsCC),
          contactsTo: renderEmails(party?.contactsTo),
          subject: party?.subject,
          emailBody: party?.emailBody
        })
        setFirstChange(true);
      }
      return;
    } catch (error) {
      mc.error(error);      
    }
  }

  const handleSelect = (value) => {
    try {
      if ( !value || value === '') return;
      needsToUpdate(allParties[index], currentParty);
      const party = allParties.find((item) => item.idPersonInsurer === value);
      setIndex(allParties.indexOf(party));
      setCurrentParty({
        idPersonInsurer: party?.idPersonInsurer,
        insurerFirstName: party?.insurerFirstName,
        contactsCC: renderEmails(party?.contactsCC),
        contactsTo: renderEmails(party?.contactsTo),
        subject: party?.subject,
        emailBody: party?.emailBody
      })
      setFirstChange(true);
    } catch (error) {
      mc.error(error);      
    }
  }

  const renderEmails = (contacts) => {
    if (!contacts.length) return '';
    return contacts.map((item) => item.email).join('; ') + ';';
  }
  
  if (!currentParty.idPersonInsurer) return null
  
  return (
    <EmailContainer>
      {
      allParties.length > 0 
      && ( 
          <Form>
            <EmailHeader>
              <TitleContainer>
                <ModalTitle>Review & Distribution</ModalTitle>
                <ContPages>
                  <Icon onClick={() => handlePrevious()} name={index === 0 ? "arrowLeftFade" : "arrowLeftWhite"} style={{ marginRight: "10px" }} />
                  <Field
                    style={{ margin: 0 }}
                    options={ options } 
                    name="idPersonInsurer"
                    component={ ComboBox }
                    onChange={ (value) => handleSelect(value) }
                    form={formikProps}
                  />
                  <Icon onClick={() => handleNext()} name={(index + 1) < numParties ? "arrowRightWhite" : "arrowRightFade"} style={{ marginLeft: "10px" }} />
                </ContPages>
              </TitleContainer>
            </EmailHeader>
            <EmailContent>
              { children }
            </EmailContent>
            <ButtonContainer
              style={{ justifyContent: "flex-end", margin: "0 20px 20px 0" }}
            >
              <ButtonContent>
                <Button
                  style={{ marginRight: "30px"}}
                  primary
                  type="button"
                  id="btnUpdate"
                  onClick={ () => handleUpdateEmailContent(currentParty) }
                >
                  Update
                </Button>
              </ButtonContent>
            </ButtonContainer>
          </Form>
      )
    } 
    </EmailContainer>
  )
}

EmailModal.propTypes = {
  contactsParties: PropTypes.arrayOf(PropTypes.shape({
    idPersonInsurer: PropTypes.number,
    contactsCC: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string
    })),
    contactsTo: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string
    })),
    subject: PropTypes.string,
    emailBody: PropTypes.string
  })),
  currentParty: PropTypes.shape({
    idPersonInsurer: PropTypes.number,
    insurerFirstName: PropTypes.string,
    contactsCC: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string
    })),
    contactsTo: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string
    })),
    subject: PropTypes.string,
    emailBody: PropTypes.string
  }),
  setCurrentParty: PropTypes.func,
  handleUPdateEmailContent: PropTypes.func
}

EmailModal.defaultProps = {
  contactsParties: [],
  currentParty: {},
  setCurrentParty: () => {},
  handleUPdateEmailContent: () => {}
}

export default EmailModal